import type { PropsWithChildren, ReactNode } from 'react'
import { useInView, type IntersectionOptions } from 'react-intersection-observer'
// reason: we need in-view in some @dhl-ui/components and do not want circular deps and box in components is shadow one pointing to same as well :)
// eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
import { Box } from '@mui/material'

export type InviewProps = PropsWithChildren<{
  /**
   * To prevent layout shift you can give a placeholder which simulates the content which should be shown.
   * Can also be the loading state of the component for example.
   */
  placeholder?: ReactNode

  /**
   * The IntersectionObserver interface's read-only root property identifies the Element or Document whose bounds are
   * treated as the bounding box of the viewport for the element which is the observer's target.
   * If the root is null, then the bounds of the actual document viewport are used.
   */
  root?: IntersectionOptions['root']

  /**
   * Set of values serves to grow or shrink each side of the bounds of the actual document viewport before computing intersections
   * @example '20px 0px 280px 0px' (for example says if its 280px under the visible view then start loading)
   * @default '40px 40px 280px 0px'
   */
  rootMargin?: string

  /**
   * Sometimes you need to target the parent its container
   */
  sx?: {
    width?: string | number
    height?: string | number
  }
}>

/**
 * Client side component rendering something which becomes into our visible viewport
 * You have the controls by using the rootMargin when it should start loading from our viewport
 *
 * @example
 * ```jsx
 * import React from 'react'
 * import { useInView } from '@dhl-ui/in-view'
 *
 * const FancyComponent = () => {
 *   ...
 *   return (
 *     ...
 *        <InView placeholder={<Skeleton />}>
 *           <img src="/dhl/welcome">
 *        </Inview>
 *     ...
 *   );
 * };
 * ```
 */

export const InView = ({ children, placeholder = null, root, rootMargin, sx }: InviewProps) => {
  const margin = rootMargin && /((((.\d*)?(px))){4})/.test(rootMargin) ? rootMargin : '40px 40px 280px 0px'

  const [ref, inView] = useInView({
    root,
    threshold: 0,
    triggerOnce: true,
    rootMargin: margin,
  })

  return (
    <Box ref={ref} sx={sx}>
      {Boolean(!inView) && placeholder}
      {inView && children}
    </Box>
  )
}
